import { createNativeInstance } from '@transifex/native';

/**
 * The dynamicAssessments module's own Transifex Native instance.
 *
 * `@transifex/native` exposes a process-wide default `tx`, and `useT()` / `<T>`
 * bind to it unless a `TXProvider` scopes them to something else. That default is
 * shared mutable state: a host app calling `tx.init({ token: theirs })` replaces
 * the token our strings were fetched under, and every assessment string silently
 * falls back to its English source. Nothing throws, nothing logs — the screens
 * just stop being translated.
 *
 * This is the same class of problem as the api client and the analytics sink,
 * and it gets the same treatment: the module owns its dependency instead of
 * reaching for an ambient one. `DynamicAssessmentProvider` scopes `useT()` to this instance
 * via `TXProvider`, so our translations and a host's can't collide in either
 * direction — we don't read their token and we never touch it.
 *
 * Created with an empty token at import so the instance always exists;
 * `configureAssessmentTxToken` supplies the real one at provider mount. A missing
 * token is not an error — it renders source strings, which is the correct
 * degradation for a host that hasn't wired translations yet.
 *
 * See `docs/packaging-plan.md` §9.6.
 */
export const assessmentTx = createNativeInstance({ token: '' });

/**
 * Point `assessmentTx` at a token, idempotently.
 *
 * Called from a layout effect in `DynamicAssessmentProvider`, not during render: `init()`
 * mutates this module singleton, and render must stay pure. A layout effect runs
 * before paint and before any child's `useT()` subscription — `@transifex/react`
 * subscribes in a passive `useEffect`, which React flushes after all layout
 * effects.
 *
 * Re-initializing with an unchanged token would discard the fetched translation
 * cache, so the equality check is load-bearing rather than a micro-optimization.
 * Not because the caller's effect re-fires on an unchanged token — `transifexToken`
 * is a string compared by value in a `[transifexToken]` dependency array, so it
 * does not. It matters for the calls that DO repeat with the same token: a
 * remounted `DynamicAssessmentProvider` (a second one mounted elsewhere in the tree, or the
 * same one after a route change), and StrictMode's deliberate double-invocation of
 * effects in development.
 */
export const configureAssessmentTxToken = (token?: string): void => {
  const next = token ?? '';
  if (assessmentTx.token === next) return;
  assessmentTx.init({ token: next });
};
